import type { ReactNode } from "react";
import { Icon } from "./Icons";

type FeedbackTone = "empty" | "loading" | "error" | "success";

export interface FeedbackStateProps {
  action?: ReactNode;
  compact?: boolean;
  description?: ReactNode;
  icon?: ReactNode;
  live?: "off" | "polite" | "assertive";
  title: ReactNode;
  tone: FeedbackTone;
}

export function FeedbackState({ action, compact, description, icon, live, title, tone }: FeedbackStateProps) {
  const defaultIcon = tone === "success"
    ? <Icon name="check" />
    : tone === "error"
      ? <Icon name="warning" />
      : undefined;
  const liveSetting = live ?? (tone === "error" ? "assertive" : tone === "loading" ? "polite" : "off");

  return (
    <div
      aria-live={liveSetting}
      aria-busy={tone === "loading" || undefined}
      className={`am-feedback am-feedback--${tone}${compact ? " am-feedback--compact" : ""}`}
      role={tone === "error" ? "alert" : tone === "success" ? "status" : undefined}
    >
      <div aria-hidden="true" className="am-feedback__icon">
        {tone === "loading" ? <span className="am-spinner" /> : icon ?? defaultIcon ?? <span className="am-feedback__empty-mark" />}
      </div>
      <div className="am-feedback__copy">
        <h3>{title}</h3>
        {description && <div className="am-feedback__description">{description}</div>}
      </div>
      {action && <div className="am-feedback__action">{action}</div>}
    </div>
  );
}

export type SimpleFeedbackProps = Omit<FeedbackStateProps, "tone">;

export function EmptyState(props: SimpleFeedbackProps) {
  return <FeedbackState {...props} tone="empty" />;
}

export function LoadingState({ title = "Loading", ...props }: Omit<SimpleFeedbackProps, "title"> & { title?: ReactNode }) {
  return <FeedbackState {...props} title={title} tone="loading" />;
}

export function ErrorState(props: SimpleFeedbackProps) {
  return <FeedbackState {...props} tone="error" />;
}

export function SuccessState(props: SimpleFeedbackProps) {
  return <FeedbackState {...props} tone="success" />;
}

export interface SkeletonProps {
  height?: string;
  label?: string;
  width?: string;
}

export function Skeleton({ height, label = "Loading content", width }: SkeletonProps) {
  return (
    <span
      aria-label={label}
      className="am-skeleton"
      role="status"
      style={{ height, width }}
    />
  );
}

